import React, { useState } from "react";
import api from "../lib/api";
import {
  Loader2, FileCheck, CheckCircle, AlertTriangle, AlertCircle, Info, ExternalLink, ChevronDown,
} from "lucide-react";
import { toast } from "sonner";
import { categoriseChecks } from "../lib/onpageChecks";

/**
 * On-page audit — ad-hoc DataForSEO instant audit on any URL.
 * Not tied to a tracked client — handy for prospecting / spot-checking
 * competitor pages / debugging a specific client page that isn't the homepage.
 */
export default function OnPageAudit() {
  const [url, setUrl] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [auditedUrl, setAuditedUrl] = useState(null);

  const run = async (e) => {
    e?.preventDefault();
    const u = url.trim();
    if (!u) return;
    setLoading(true);
    setData(null);
    try {
      // The /seo/on-page endpoint accepts either a bare domain (auto-prefixed
      // with https://) or any full URL. Either input shape works.
      const { data: resp } = await api.post("/seo/on-page", {
        domain: u,
        max_pages: 1,
      });
      setData(resp?.result || null);
      setAuditedUrl(u);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "On-page audit failed");
    } finally {
      setLoading(false);
    }
  };

  const item = data?.items?.[0] || {};
  const onpage = item.onpage_score;
  const checks = item.checks || {};
  const meta = item.meta || {};
  const { issues, passing, info } = categoriseChecks(checks);

  return (
    <div className="space-y-5 pt-2" data-testid="onpage-audit-page">
      <div>
        <div className="text-[11px] uppercase tracking-[0.16em] text-[#a0aec0] font-semibold">
          Pages / Tools
        </div>
        <h1 className="font-display text-3xl mt-1 text-[#1a202c]">On-page audit</h1>
        <p className="text-sm text-[#718096] mt-1.5">
          Instant on-page audit for any URL — score, meta, and issues detected.
        </p>
      </div>

      <form onSubmit={run} className="flex gap-2 flex-wrap">
        <div className="flex-1 min-w-[280px] relative">
          <FileCheck className="h-3.5 w-3.5 absolute left-4 top-1/2 -translate-y-1/2 text-[#a0aec0]" />
          <input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="example.com or https://example.com/specific-page"
            data-testid="onpage-url-input"
            className="w-full bg-white border border-[#e2e8f0] rounded-xl pl-10 pr-3 py-2.5 text-sm font-mono-tight focus:outline-none focus:border-[#4fd1c5] focus:ring-4 focus:ring-[#4fd1c5]/15 transition-all"
          />
        </div>
        <button
          type="submit"
          disabled={loading || !url.trim()}
          data-testid="onpage-run-button"
          className="btn-primary inline-flex items-center gap-2"
        >
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileCheck className="h-3.5 w-3.5" />}
          Audit
        </button>
      </form>

      <div className="text-[11px] text-[#a0aec0]">
        Tip: paste a full URL (with <span className="font-mono-tight">https://</span>) to audit a specific page, or just a domain to audit the homepage.
      </div>

      {loading && (
        <div className="text-sm text-[#a0aec0] font-mono-tight flex items-center gap-2 mt-4">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Running on-page audit on {url}…
        </div>
      )}

      {data && !loading && (
        <div className="space-y-5 mt-4" data-testid="onpage-results">
          <div className="text-[11px] text-[#a0aec0] font-mono-tight">
            Audited:{" "}
            <a
              href={item.url || auditedUrl}
              target="_blank"
              rel="noreferrer"
              className="hover:text-[#2c7a7b] inline-flex items-center gap-1"
            >
              {item.url || auditedUrl}
              <ExternalLink className="h-2.5 w-2.5" />
            </a>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <div className="card-soft p-5">
              <div className="text-[11px] uppercase tracking-widest text-[#a0aec0] font-semibold">
                On-page score
              </div>
              <div className="mt-3 flex items-baseline gap-2">
                <div className="font-display text-5xl text-[#1a202c]">
                  {onpage != null ? Math.round(onpage) : "—"}
                </div>
                <div className="text-[#a0aec0] text-sm">/ 100</div>
              </div>
              <div className="mt-3 h-2 bg-[#edf2f7] rounded-full overflow-hidden">
                <div
                  className="h-full bg-brand transition-all rounded-full"
                  style={{ width: `${Math.min(100, onpage || 0)}%` }}
                />
              </div>
            </div>
            <Card title="Status code" value={item.status_code ?? "—"} ok={item.status_code === 200} testid="onpage-status" />
            <Card
              title="Page weight"
              value={meta.content?.plain_text_size != null ? `${Math.round(meta.content.plain_text_size / 1024)} KB` : "—"}
              testid="onpage-weight"
            />
          </div>

          <div className="card-soft overflow-hidden">
            <div className="px-5 py-4 border-b border-[#edf2f7]">
              <div className="text-[11px] uppercase tracking-widest text-[#a0aec0] font-semibold">
                page meta
              </div>
              <h3 className="font-display text-base mt-0.5 text-[#1a202c] break-all">{item.url}</h3>
            </div>
            <div className="p-5 space-y-3 text-sm">
              <Row k="Title" v={meta.title} />
              <Row k="Description" v={meta.description} />
              <Row k="H1" v={(meta.htags?.h1 || []).join(" | ")} />
              <Row k="Canonical" v={meta.canonical} />
              <Row k="Word count" v={meta.content?.plain_text_word_count} mono />
              <Row k="Internal links" v={meta.internal_links_count} mono />
              <Row k="External links" v={meta.external_links_count} mono />
              <Row k="Images" v={meta.images_count} mono />
            </div>
          </div>

          <div className="card-soft overflow-hidden" data-testid="onpage-issues-card">
            <div className="px-5 py-4 border-b border-[#edf2f7]">
              <div className="text-[11px] uppercase tracking-widest text-[#a0aec0] font-semibold">
                Issues detected
              </div>
              <h3 className="font-display text-base mt-0.5 text-[#1a202c]">
                {issues.length} issue{issues.length === 1 ? "" : "s"} to review
              </h3>
            </div>
            <div className="divide-y divide-[#edf2f7]">
              {issues.length === 0 && (
                <div className="p-6 text-center text-sm text-[#48bb78] flex items-center justify-center gap-2">
                  <CheckCircle className="h-4 w-4" /> No real issues detected.
                </div>
              )}
              {issues.map((issue, i) => (
                <IssueRow key={issue.key} issue={issue} testid={`onpage-issue-${i}`} />
              ))}
            </div>
          </div>

          {passing.length > 0 && (
            <CollapsibleSection
              title={`${passing.length} check${passing.length === 1 ? "" : "s"} passing`}
              eyebrow="Best practices in place"
              tone="good"
              testid="onpage-passing-card"
              defaultOpen={false}
            >
              {passing.map((c, i) => (
                <IssueRow key={c.key} issue={c} testid={`onpage-pass-${i}`} />
              ))}
            </CollapsibleSection>
          )}

          {info.length > 0 && (
            <CollapsibleSection
              title={`${info.length} informational note${info.length === 1 ? "" : "s"}`}
              eyebrow="Worth a glance"
              tone="info"
              testid="onpage-info-card"
              defaultOpen={false}
            >
              {info.map((c, i) => (
                <IssueRow key={c.key} issue={c} testid={`onpage-info-${i}`} />
              ))}
            </CollapsibleSection>
          )}
        </div>
      )}
    </div>
  );
}

function severityStyles(sev) {
  switch (sev) {
    case "error":
      return { Icon: AlertCircle, color: "#e53e3e", bg: "#fed7d7", label: "ERROR" };
    case "warning":
      return { Icon: AlertTriangle, color: "#dd6b20", bg: "#feebc8", label: "WARNING" };
    case "info":
      return { Icon: Info, color: "#3182ce", bg: "#bee3f8", label: "INFO" };
    case "good":
      return { Icon: CheckCircle, color: "#38a169", bg: "#c6f6d5", label: "PASSING" };
    default:
      return { Icon: AlertTriangle, color: "#a0aec0", bg: "#edf2f7", label: "" };
  }
}

function IssueRow({ issue, testid }) {
  const [open, setOpen] = useState(false);
  const { Icon, color, bg, label } = severityStyles(issue.severity);
  return (
    <div className="px-5 py-3" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 text-sm text-left hover:opacity-80 transition-opacity"
        aria-expanded={open}
      >
        <Icon className="h-4 w-4 shrink-0" style={{ color }} strokeWidth={2.2} />
        <span className="font-semibold text-[#1a202c] flex-1">{issue.label}</span>
        <span
          className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded-md shrink-0"
          style={{ color, background: bg }}
        >
          {label}
        </span>
        <ChevronDown
          className={`h-4 w-4 text-[#a0aec0] shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
          strokeWidth={2}
        />
      </button>
      {open && (
        <div className="mt-3 ml-7 pl-3 border-l-2 space-y-3 text-sm" style={{ borderColor: bg }}>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#a0aec0] font-bold mb-1">What it means</div>
            <p className="text-[#4a5568] leading-relaxed">{issue.explanation}</p>
          </div>
          {issue.fix && (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#a0aec0] font-bold mb-1">How to fix</div>
              <p className="text-[#1a202c] leading-relaxed">{issue.fix}</p>
            </div>
          )}
          <div className="text-[10px] text-[#a0aec0] font-mono-tight">DFS check: {issue.key}</div>
        </div>
      )}
    </div>
  );
}

function CollapsibleSection({ title, eyebrow, tone, children, testid, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const tones = {
    good: { color: "#38a169", border: "#c6f6d5", Icon: CheckCircle },
    info: { color: "#3182ce", border: "#bee3f8", Icon: Info },
  };
  const t = tones[tone] || tones.info;
  const Icon = t.Icon;
  return (
    <div className="card-soft overflow-hidden" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full px-5 py-4 flex items-center gap-3 text-left hover:bg-[#f7fafc] transition-colors"
        aria-expanded={open}
      >
        <Icon className="h-4 w-4 shrink-0" style={{ color: t.color }} strokeWidth={2.2} />
        <div className="flex-1">
          <div className="text-[11px] uppercase tracking-widest text-[#a0aec0] font-semibold">{eyebrow}</div>
          <h3 className="font-display text-base mt-0.5 text-[#1a202c]">{title}</h3>
        </div>
        <ChevronDown
          className={`h-4 w-4 text-[#a0aec0] shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
          strokeWidth={2}
        />
      </button>
      {open && <div className="divide-y divide-[#edf2f7] border-t border-[#edf2f7]">{children}</div>}
    </div>
  );
}

function Card({ title, value, ok, testid }) {
  return (
    <div className="card-soft p-5" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-widest text-[#a0aec0] font-semibold">{title}</div>
      <div className={`mt-3 font-display text-3xl ${ok ? "text-[#48bb78]" : "text-[#1a202c]"}`}>
        {value}
      </div>
    </div>
  );
}

function Row({ k, v, mono }) {
  return (
    <div className="grid grid-cols-12 gap-3 py-1.5">
      <div className="col-span-3 text-[11px] uppercase tracking-widest text-[#a0aec0] font-semibold">{k}</div>
      <div className={`col-span-9 ${mono ? "font-mono-tight" : ""} text-[#1a202c] break-words`}>
        {v || <em className="text-[#a0aec0]">empty</em>}
      </div>
    </div>
  );
}
